import { useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
    ORGANIZATION_FUNCTION_ERROR_CODES,
    getOrganizationFunctionErrorCode,
    isDpo,
    isReservedFunction,
} from "src/modules/data-protection/constants/organizationFunctions";
import { ROUTE_PATH_ADMIN_USER_LIST } from "src/modules/data-protection/constants";

import useOrganizationFunction from "./useOrganizationFunction";
import useOrganizationFunctionMutations from "./useOrganizationFunctionMutations";

const FUNCTIONS_LIST_PATH = `${ROUTE_PATH_ADMIN_USER_LIST}?tab=functions`;

const useFunctionDetailsPage = (t) => {
    const { key } = useParams();
    const history = useHistory();
    const initializedKeyRef = useRef(null);

    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [titleError, setTitleError] = useState(false);
    const [pendingDpoUser, setPendingDpoUser] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const functionQuery = useOrganizationFunction(key, t);
    const organizationFunction = functionQuery.data;
    const mutations = useOrganizationFunctionMutations({
        functionKey: organizationFunction?.key || key,
        t,
    });

    useEffect(() => {
        if (!organizationFunction) {
            return;
        }

        if (initializedKeyRef.current !== organizationFunction.key) {
            setTitle(organizationFunction.title || "");
            setDescription(organizationFunction.description || "");
            setPendingDpoUser(null);
            setTitleError(false);
            initializedKeyRef.current = organizationFunction.key;
        }
    }, [organizationFunction]);

    const assignedUsers = Array.isArray(organizationFunction?.assigned_users)
        ? organizationFunction.assigned_users
        : [];
    const isDpoFunction = isDpo(organizationFunction?.key);
    const isReserved = isReservedFunction(organizationFunction);
    const isDpoUnassigned = isDpoFunction && assignedUsers.length === 0;

    const handleCancel = () => {
        history.push(FUNCTIONS_LIST_PATH);
    };

    const handleSave = async () => {
        const trimmedTitle = (title || "").trim();

        if (!trimmedTitle) {
            setTitleError(true);
            toast(t(ORGANIZATION_FUNCTION_ERROR_CODES.TITLE_REQUIRED), {
                type: "error",
            });
            return;
        }

        setTitleError(false);

        try {
            await mutations.updateMutation.mutateAsync({
                title: trimmedTitle,
                description: description || "",
            });

            if (isDpoUnassigned && pendingDpoUser?.value) {
                try {
                    await mutations.assignMutation.mutateAsync(
                        pendingDpoUser.value
                    );
                    setPendingDpoUser(null);
                } catch (assignError) {
                    const code = getOrganizationFunctionErrorCode(assignError);

                    if (
                        code ===
                        ORGANIZATION_FUNCTION_ERROR_CODES.DPO_ALREADY_ASSIGNED
                    ) {
                        toast(t(code), {
                            type: "error",
                        });
                        await mutations.replaceDpoMutation.mutateAsync(
                            pendingDpoUser.value
                        );
                        setPendingDpoUser(null);
                    } else {
                        throw assignError;
                    }
                }
            }

            mutations.invalidateFunctionQueries();
            toast(t("Data was saved successfully"), {
                type: "success",
            });
        } catch (error) {
            if (process.env.NODE_ENV === "development") {
                console.error(error);
            }
        }
    };

    const isSaving =
        mutations.updateMutation.isLoading ||
        mutations.assignMutation.isLoading ||
        mutations.replaceDpoMutation.isLoading;

    return {
        key,
        functionQuery,
        organizationFunction,
        title,
        setTitle,
        description,
        setDescription,
        titleError,
        setTitleError,
        pendingDpoUser,
        setPendingDpoUser,
        isDeleteOpen,
        setIsDeleteOpen,
        assignedUsers,
        isDpoFunction,
        isReserved,
        isDpoUnassigned,
        handleCancel,
        handleSave,
        isSaving,
        mutations,
        functionsListPath: FUNCTIONS_LIST_PATH,
        usersListPath: `${ROUTE_PATH_ADMIN_USER_LIST}?tab=users`,
    };
};

export default useFunctionDetailsPage;
